<template>
    <div class="payment-modal">
        <form @submit.prevent="handleSubmit" class="stripe-form">
            <p class="text-zinc-950 text-lg font-semibold">Add Payment Method</p>
            <div class="divider"></div>
            <div>
                <div class="name-container">
                    <div class="name-div">
                        <label>First name</label>
                        <PrimeInputText
                            class="w-full"
                            placeholder="John"
                            :invalid="submitted && !firstName.trim()"
                            v-model="firstName"
                            @keydown="letterOnlyInputHandler"
                            @paste="letterOnlyInputHandler"
                        />
                        <div v-if="submitted && !firstName.trim()" class="validation-message">
                            <span>First name is required.</span>
                        </div>
                    </div>
                    <div class="name-div">
                        <label>Last name</label>
                        <PrimeInputText
                            class="w-full"
                            placeholder="Doe"
                            :invalid="submitted && !lastName.trim()"
                            v-model="lastName"
                            @keydown="letterOnlyInputHandler"
                            @paste="letterOnlyInputHandler"
                        />
                        <div v-if="submitted && !lastName.trim()" class="validation-message">
                            <span>Last name is required.</span>
                        </div>
                    </div>
                </div>
                <div id="card-element"></div>
            </div>
            <div class="flex justify-end mt-4 gap-2">
                <PrimeButton
                    label="Cancel"
                    @click="closeDialog"
                    variant="secondary"
                    class="p-btn-root_secondary"
                    :disabled="paymentLoader"
                />
                <PrimeButton label="Add" type="submit" :loading="paymentLoader" :disabled="!canSubmit" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button';
import PrimeInputText from 'primevue/inputtext';

interface ProposalPaymentResult {
    paymentMethodId: string;
    firstName: string;
    lastName: string;
}

const emit = defineEmits<{
    (e: 'submitted', payload: ProposalPaymentResult): void;
}>();

defineProps<{
    onClose?: () => void;
}>();

const { letterOnlyInputHandler } = useUtils();
const { showError } = useGlobalToast();
const config = useRuntimeConfig();

const paymentLoader = ref(false);
const stripe = ref<any>();
const elements = ref<any>();
const firstName = ref('');
const lastName = ref('');
const submitted = ref(false);
const isCardComplete = ref(false);

const dialogRef = inject<any>('dialogRef');

const canSubmit = computed(
    () => firstName.value.trim() !== '' && lastName.value.trim() !== '' && isCardComplete.value
);

function closeDialog() {
    dialogRef?.value?.close();
}

onMounted(async () => {
    const stripeModule = await import('@stripe/stripe-js');
    // `loadStripe` resolves to `null` when the publishable key is invalid
    // or the Stripe script is blocked (ad-blockers, network policies).
    // Surface that as a toast instead of throwing on `.elements(...)`,
    // which would leave the modal mounted with no card form.
    stripe.value = await stripeModule.loadStripe(config.public.pb.stripeKey);
    if (!stripe.value) {
        showError('Error', 'Payment form failed to load. Please disable ad blockers and try again.');
        return;
    }

    elements.value = stripe.value.elements({
        mode: 'setup',
        currency: 'usd',
        paymentMethodCreation: 'manual',
        paymentMethodTypes: ['card'],
    });

    const card = elements.value.create('payment', {
        fields: { billingDetails: 'auto' },
        wallets: { googlePay: 'never', applePay: 'never', link: 'never' },
    });
    card.mount('#card-element');
    card.on('change', (event: any) => {
        isCardComplete.value = event.complete;
    });
});

async function handleSubmit() {
    if (paymentLoader.value) return;
    submitted.value = true;

    if (!firstName.value || !lastName.value) return;
    if (!stripe.value || !elements.value) {
        showError('Error', 'Payment form failed to load. Please refresh and try again.');
        return;
    }

    paymentLoader.value = true;

    let paymentMethodResult: any;
    try {
        // `elements.submit()` can reject on transient Stripe / network
        // failures. Without this guard, paymentLoader stays true and the
        // submit/cancel controls remain disabled until the modal is reopened.
        await elements.value.submit();
        paymentMethodResult = await stripe.value.createPaymentMethod({
            elements: elements.value,
            params: {
                billing_details: {
                    name: `${firstName.value} ${lastName.value}`,
                },
            },
        });
    } catch (err: any) {
        paymentLoader.value = false;
        showError('Error', err?.message || 'Could not submit payment details.');
        return;
    }

    const { error, paymentMethod } = paymentMethodResult;

    if (error) {
        paymentLoader.value = false;
        showError('Error', `Payment error: ${error.message}`);
        return;
    }

    const countryCode = paymentMethod?.billing_details?.address?.country;
    if (['RU', 'UA', 'BY'].includes(countryCode ?? '')) {
        showError(
            'Unsupported Country',
            'Payments from this country are currently restricted under international regulations.'
        );
        paymentLoader.value = false;
        return;
    }

    if (!paymentMethod?.id) {
        paymentLoader.value = false;
        return;
    }

    const dialogData = dialogRef?.value?.data as
        | { onSubmitted?: (payload: ProposalPaymentResult) => Promise<boolean | void> | boolean | void }
        | undefined;

    const payload: ProposalPaymentResult = {
        paymentMethodId: paymentMethod.id,
        firstName: firstName.value,
        lastName: lastName.value,
    };

    try {
        if (dialogData?.onSubmitted) {
            const result = await dialogData.onSubmitted(payload);
            if (result === false) return;
        }
        emit('submitted', payload);
        closeDialog();
    } catch (error: any) {
        showError('Payment failed', error?.message || 'Unable to save payment method.');
    } finally {
        paymentLoader.value = false;
    }
}
</script>

<style lang="scss" scoped>
.payment-modal {
    @apply w-full mx-auto rounded-lg bg-white p-8;
    max-width: 100%;
    box-sizing: border-box;
}

.name-div {
    @apply flex flex-col mb-3;
    min-width: 0;
    flex: 1 1 0;

    label {
        @apply text-zinc-800 text-xs mb-1;
    }

    :deep(.p-inputtext) {
        width: 100%;
        min-width: 0;
    }
}

.divider {
    @apply h-px self-stretch bg-zinc-300 my-5;
}

.name-container {
    @apply flex flex-col lg:flex-row gap-1 lg:gap-4;
    width: 100%;
    min-width: 0;
}

.stripe-form {
    @apply w-full;
}

.validation-message {
    @apply flex gap-1 mt-1;

    span {
        @apply text-red-600 text-sm font-normal leading-5;
    }
}
</style>
